import { Injectable } from '@angular/core';

import * as THREE from 'three';

import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

import DxfParser from 'dxf-parser';

import {
  DxfLoadResult,
  LocalOrigin,
  Point3D
} from './volume-viewer.models';

interface DxfVertex {
  x?: number;
  y?: number;
  z?: number;
}

interface DxfEntity {
  type?: string;
  vertices?: DxfVertex[];
  points?: DxfVertex[];
  start?: DxfVertex;
  end?: DxfVertex;
  elevation?: number;
  position?: DxfVertex;
  closed?: boolean;
  shape?: boolean;
  flags?: number;
}

interface ParsedDxf {
  entities?: DxfEntity[];
}

export interface VolumeDxfLoaderOptions {
  lineColor: number;
  lineWidth: number;
}

@Injectable({
  providedIn: 'root'
})
export class VolumeDxfLoaderService {

  load(
    text: string,
    origin: LocalOrigin,
    options: VolumeDxfLoaderOptions
  ): DxfLoadResult {

    const parser = new DxfParser();

    const parsed = parser.parseSync(
      text
    ) as ParsedDxf | null;

    if (!parsed) {
      throw new Error(
        'Le fichier DXF est invalide.'
      );
    }

    const group = new THREE.Group();
    group.name = 'DXF lines';

    let lineCount = 0;

    for (const entity of parsed.entities ?? []) {

      const polylines =
        this.extractEntity(entity);

      for (const polyline of polylines) {

        if (polyline.length < 2) {
          continue;
        }

        const positions: number[] = [];

        for (const point of polyline) {
          positions.push(
            point.x - origin.x,
            point.y - origin.y,
            point.z - origin.z
          );
        }

        const geometry =
          new LineGeometry();

        geometry.setPositions(
          positions
        );

        const material =
          new LineMaterial({
            color: options.lineColor,
            linewidth: options.lineWidth,
            worldUnits: false
          });

        const line =
          new Line2(
            geometry,
            material
          );

        line.computeLineDistances();

        line.name =
          entity.type ?? 'DXF line';

        group.add(line);

        lineCount++;
      }
    }

    if (lineCount === 0) {
      throw new Error(
        'Le DXF ne contient aucune entité LINE, ' +
        'LWPOLYLINE ou POLYLINE exploitable.'
      );
    }

    return {
      group,
      lineCount
    };
  }

  private extractEntity(
    entity: DxfEntity
  ): Point3D[][] {

    switch (entity.type) {

      case 'LINE':
        return this.extractLine(
          entity
        );

      case 'LWPOLYLINE':
      case 'POLYLINE':
        return this.extractPolyline(
          entity
        );

      default:
        return [];
    }
  }

  private extractLine(
    entity: DxfEntity
  ): Point3D[][] {

    const vertices =
      entity.vertices ??
      entity.points ??
      [];

    if (vertices.length >= 2) {

      const start =
        this.toPoint(
          vertices[0],
          entity
        );

      const end =
        this.toPoint(
          vertices[1],
          entity
        );

      return start && end
        ? [[start, end]]
        : [];
    }

    const start =
      this.toPoint(
        entity.start,
        entity
      );

    const end =
      this.toPoint(
        entity.end,
        entity
      );

    return start && end
      ? [[start, end]]
      : [];
  }

  private extractPolyline(
    entity: DxfEntity
  ): Point3D[][] {

    const vertices =
      entity.vertices ??
      entity.points ??
      [];

    const points = vertices
      .map(vertex =>
        this.toPoint(
          vertex,
          entity
        )
      )
      .filter(
        (
          point
        ): point is Point3D =>
          point !== undefined
      );

    if (points.length < 2) {
      return [];
    }

    if (this.isClosed(entity)) {

      points.push({
        ...points[0]
      });
    }

    return [points];
  }

  private toPoint(
    vertex: DxfVertex | undefined,
    entity: DxfEntity
  ): Point3D | undefined {

    if (!vertex) {
      return undefined;
    }

    const x =
      this.toFiniteNumber(
        vertex.x
      );

    const y =
      this.toFiniteNumber(
        vertex.y
      );

    if (
      x === undefined ||
      y === undefined
    ) {
      return undefined;
    }

    const elevation =
      this.toFiniteNumber(
        entity.elevation
      ) ??
      this.toFiniteNumber(
        entity.position?.z
      ) ??
      0;

    return {
      x,
      y,
      z:
        this.toFiniteNumber(
          vertex.z
        ) ??
        elevation
    };
  }

  private isClosed(
    entity: DxfEntity
  ): boolean {

    return Boolean(
      entity.closed ||
      entity.shape ||
      ((entity.flags ?? 0) & 1)
    );
  }

  private toFiniteNumber(
    value: unknown
  ): number | undefined {

    const numberValue =
      Number(value);

    return Number.isFinite(
      numberValue
    )
      ? numberValue
      : undefined;
  }
}
